import { Text, View } from 'native-base';
import React, { ReactNode, useMemo } from 'react';

import HTML, { HTMLRendererProps } from 'app/components/HTML';
import useToggle from 'app/hooks/useToggle';
import { useI18n } from 'app/providers/I18nProvider';

export type HTMLCollapsibleProps = {
  children?: ReactNode;
  html: string;
  numberOfLines: number;
  initialOpen?: boolean;
  onExpand?: () => void;
} & HTMLRendererProps;

const APPROX_CHARS_PER_LINE = 50;

export default function HTMLCollapsible({
  html,
  initialOpen,
  numberOfLines,
  onExpand,
  ...props
}: HTMLCollapsibleProps) {
  const { t } = useI18n();
  const [expanded, toggle] = useToggle(initialOpen);
  const threshold = numberOfLines * APPROX_CHARS_PER_LINE;
  const isCollapsible = html.length > threshold;
  const truncatedHtml = useMemo(
    () => (isCollapsible && !expanded ? `${html.substring(0, threshold)}...` : html),
    [expanded, html, isCollapsible, threshold]
  );

  const handleToggle = () => {
    if (!expanded && onExpand) onExpand();
    toggle();
  };

  return (
    <View testID="HTMLCollapsible">
      <HTML {...props} defaultTextProps={props.defaultTextProps} html={truncatedHtml} />
      {isCollapsible && (
        <Text bold color={props.color} pt="2" underline onPress={handleToggle}>
          {expanded ? t('gen_toggle_show_less') : t('gen_toggle_show_more')}
        </Text>
      )}
    </View>
  );
}
